import { Box, Typography, FormControl, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { LoadingSpinner } from 'sharedComponents/LoadingSpinner/loadingSpinner';
import { styled } from '@mui/material/styles';
import { BasicSelectWithDescriptionsProps } from './types';
import { formatTestId } from 'features/Connect/utils/connectUtils';

export const BasicSelectWithDescriptions: React.FC<BasicSelectWithDescriptionsProps> = ({
  options = [],
  selectedOption,
  onChange = () => {},
  isLoading,
  dataTestId,
  size = 'medium',
  sx,
  menuSx,
}) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const renderValue = (value: string) => {
    if (isLoading) {
      return (
        <StyledRenderedValue>
          <LoadingSpinner dataTestId={`${dataTestId}-loading`} size={20} />
        </StyledRenderedValue>
      );
    }

    if (!value) {
      return <PlaceholderText>Set your status</PlaceholderText>;
    }

    const selectedOption = options.find((option) => option.value === value);
    if (!selectedOption) return value;

    return (
      <StyledRenderedValue>
        <StyledIcon iconColor={selectedOption.iconColor}>{selectedOption.icon}</StyledIcon>
        <Label>{selectedOption.label}</Label>
      </StyledRenderedValue>
    );
  };

  return (
    <FormControl sx={{ ...sx }}>
      <Select
        value={selectedOption || ''}
        onChange={handleChange}
        data-testid={dataTestId}
        renderValue={renderValue}
        size={size}
        disabled={isLoading}
        placeholder="Set your status"
        displayEmpty
        MenuProps={{
          PaperProps: {
            sx: {
              ...menuSx,
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            data-testid={`${dataTestId}-${formatTestId(option.value)}-option`}
          >
            <OptionContainer>
              <OptionLabelContainer>
                {option.icon && <StyledIcon iconColor={option.iconColor}>{option.icon}</StyledIcon>}
                <Label>{option.label}</Label>
              </OptionLabelContainer>
              <Description>{option.description}</Description>
            </OptionContainer>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const OptionContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

const OptionLabelContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
}));

const Label = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '16px',
  letterSpacing: '0.15px',
}));

const StyledIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'iconColor',
})<{ iconColor: string }>(({ theme, iconColor }) => ({
  color: iconColor,
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(1),
}));

const Description = styled(Typography)(({ theme }) => ({
  ...theme.typography.body3,
  textWrap: 'wrap',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '18px',
  letterSpacing: '0.15px',
  marginTop: '5px',
}));

const PlaceholderText = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

const StyledRenderedValue = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}));
